/**
 * Extended react-native → react-native-web shim.
 *
 * Re-exports everything from react-native-web and overrides a few APIs.
 *
 * IMPORTANT — why this enumerates exports manually instead of `export *`:
 *
 *   The previous version used `export * from 'react-native-web'` followed by
 *   `export { IOSSwitch as Switch }`. The ES spec says a local named export
 *   shadows a wildcard re-export of the same name, but webpack's CJS↔ESM
 *   interop loses that priority for default-CJS packages like react-native-web
 *   (every export ends up routed through `__webpack_require__("react-native-web")`
 *   which exposes the original `Switch` first; the local override never wins).
 *   The visible symptom: settings screens render react-native-web's default
 *   Material-style toggle (light teal track + dark teal thumb that extends past
 *   the track rim) instead of the iOS pill — the very thing IOSSwitch.web.js
 *   was created to prevent.
 *
 *   Listing each export explicitly here removes the ambiguity entirely:
 *   webpack sees a flat set of named bindings, with `Switch` and `Alert`
 *   bound to our local modules and everything else proxied to RN-Web.
 *
 * Overrides:
 *
 *   Switch  — iOS-style toggle so desktop / web match the iPhone build
 *             instead of falling through to react-native-web's default
 *             Material-style switch.
 *
 *   Alert   — react-native-web ships `Alert.alert` as a literal no-op, so
 *             ~150 call sites across the app silently do nothing on
 *             desktop. Replace with a DOM-rendered modal that matches the
 *             RN API shape.
 *
 *   Share   — react-native-web's `Share.share` calls `navigator.share`
 *             when present and otherwise rejects. Electron (file:// /
 *             app://) does not expose `navigator.share`, so every Share
 *             button across the app silently fails on desktop. Replace
 *             with a wrapper that prefers `navigator.share` when
 *             available and otherwise copies the URL/message to the
 *             clipboard with a confirmation toast.
 *
 *   unstable_batchedUpdates — removed from RN in React 18; all updates are
 *                             now batched automatically. Shim as a passthrough.
 */
import * as RNW from 'react-native-web';

import IOSSwitch from './IOSSwitch.web.js';
import WebAlert from './Alert.web.js';
import WebShare from './Share.web.js';

// ── Overrides ──────────────────────────────────────────────────────────────
export const Switch = IOSSwitch;
export const Alert = WebAlert;
export const Share = WebShare;

// React 18 batches all updates automatically; this is a no-op compatibility shim.
export function unstable_batchedUpdates(callback) {
  callback();
}

// ── Pass-through re-exports from react-native-web ──────────────────────────
// (Generated from `Object.keys(require('react-native-web'))` minus `Switch`,
// `Alert`, `__esModule`. Keep alphabetical so future RN-Web upgrades that
// add a new export are obvious in diff.)
export const AccessibilityInfo     = RNW.AccessibilityInfo;
export const ActivityIndicator     = RNW.ActivityIndicator;
export const Animated              = RNW.Animated;
export const AppRegistry           = RNW.AppRegistry;
export const AppState              = RNW.AppState;
export const Appearance            = RNW.Appearance;
export const BackHandler           = RNW.BackHandler;
export const Button                = RNW.Button;
export const CheckBox              = RNW.CheckBox;
export const Clipboard             = RNW.Clipboard;
export const DeviceEventEmitter    = RNW.DeviceEventEmitter;
export const Dimensions            = RNW.Dimensions;
export const Easing                = RNW.Easing;
export const FlatList              = RNW.FlatList;
export const I18nManager           = RNW.I18nManager;
export const Image                 = RNW.Image;
export const ImageBackground       = RNW.ImageBackground;
export const InteractionManager    = RNW.InteractionManager;
export const Keyboard              = RNW.Keyboard;
export const KeyboardAvoidingView  = RNW.KeyboardAvoidingView;
export const LayoutAnimation       = RNW.LayoutAnimation;
export const Linking               = RNW.Linking;
export const LogBox                = RNW.LogBox;
export const Modal                 = RNW.Modal;
export const NativeEventEmitter    = RNW.NativeEventEmitter;
export const NativeModules         = RNW.NativeModules;
export const PanResponder          = RNW.PanResponder;
export const Picker                = RNW.Picker;
export const PixelRatio            = RNW.PixelRatio;
export const Platform              = RNW.Platform;
export const Pressable             = RNW.Pressable;
export const ProgressBar           = RNW.ProgressBar;
export const RefreshControl        = RNW.RefreshControl;
export const SafeAreaView          = RNW.SafeAreaView;
export const ScrollView            = RNW.ScrollView;
export const SectionList           = RNW.SectionList;
export const StatusBar             = RNW.StatusBar;
export const StyleSheet            = RNW.StyleSheet;
export const Text                  = RNW.Text;
export const TextInput             = RNW.TextInput;
export const Touchable             = RNW.Touchable;
export const TouchableHighlight    = RNW.TouchableHighlight;
export const TouchableNativeFeedback = RNW.TouchableNativeFeedback;
export const TouchableOpacity      = RNW.TouchableOpacity;
export const TouchableWithoutFeedback = RNW.TouchableWithoutFeedback;
export const UIManager             = RNW.UIManager;
export const Vibration             = RNW.Vibration;
export const View                  = RNW.View;
export const VirtualizedList       = RNW.VirtualizedList;
export const YellowBox             = RNW.YellowBox;
export const findNodeHandle        = RNW.findNodeHandle;
export const processColor          = RNW.processColor;
export const render                = RNW.render;
export const unmountComponentAtNode = RNW.unmountComponentAtNode;
export const unstable_createElement = RNW.unstable_createElement;
export const useColorScheme        = RNW.useColorScheme;
export const useLocaleContext      = RNW.useLocaleContext;
export const useWindowDimensions   = RNW.useWindowDimensions;
